import { useCallback, useEffect, useState } from 'react';
import { GridPaginationModel } from '@mui/x-data-grid';

import { loadJsonFromLocalStorage, saveJsonToLocalStorage } from '../services/utils/localStorageState';

const DEFAULT_PAGE_SIZE = 10;

const normalizePageSize = (value: any, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

// Houdt de door de gebruiker gekozen paginagrootte per overzicht bij in localStorage.
export const usePersistedPageSize = (storageKey: string, defaultPageSize: number = DEFAULT_PAGE_SIZE) => {
  const [pageSize, setPageSizeState] = useState<number>(() =>
    loadJsonFromLocalStorage<number>(storageKey, defaultPageSize, (value) => normalizePageSize(value, defaultPageSize))
  );

  useEffect(() => {
    const loaded = loadJsonFromLocalStorage<number>(
      storageKey,
      defaultPageSize,
      (value) => normalizePageSize(value, defaultPageSize)
    );
    setPageSizeState(loaded);
  }, [storageKey, defaultPageSize]);

  const setPageSize = useCallback((size: number) => {
    const normalized = normalizePageSize(size, defaultPageSize);
    setPageSizeState(normalized);
    saveJsonToLocalStorage(storageKey, normalized);
  }, [storageKey, defaultPageSize]);

  return { pageSize, setPageSize };
};

// Handige variant die een volledig paginationModel beheert en enkel de paginagrootte persisteert.
export const usePersistedPaginationModel = (storageKey: string, defaultPageSize: number = DEFAULT_PAGE_SIZE) => {
  const { pageSize, setPageSize } = usePersistedPageSize(storageKey, defaultPageSize);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize });

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0, pageSize }));
  }, [pageSize]);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model);
    setPageSize(model.pageSize);
  }, [setPageSize]);

  return { paginationModel, setPaginationModel, handlePaginationModelChange };
};
